import type { EmblaCarouselType } from 'embla-carousel'
import type { AutoplayType } from 'embla-carousel-autoplay'
import { useCallback, useEffect, useState } from 'react'
import type { CarouselConfig } from '../types'

export interface CarouselControls {
    selectedIndex: number
    slideCount: number
    scrollTo: (index: number, jump?: boolean) => void
    scrollPrevious: VoidFunction
    scrollNext: VoidFunction
    scrollPreviousDisabled: boolean
    scrollNextDisabled: boolean
    isAutoPlaying: boolean
    toggleAutoPlay: VoidFunction
}

export interface CarouselControlsProps {
    emblaApi: EmblaCarouselType | undefined
    config: CarouselConfig | undefined
}

export function useCarouselControls({ emblaApi, config }: CarouselControlsProps): CarouselControls {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [slideCount, setSlideCount] = useState(0)
    const [scrollPreviousDisabled, setScrollPreviousDisabled] = useState(true)
    const [scrollNextDisabled, setScrollNextDisabled] = useState(true)
    const [isAutoPlaying, setIsAutoPlaying] = useState(config?.autoPlay?.enabled ?? false)

    const emblaAutoplay = emblaApi?.plugins()?.['autoplay'] as AutoplayType | undefined

    const scrollTo = useCallback(
        (index, jump = false) => {
            if (index !== selectedIndex) {
                // The scrollTo function takes the shortest path to the selected snap, when loop is `true` this is not visually desirable.
                // Providing the third undocumented parameter allows us specify  direction.
                // https://github.com/davidjerleke/embla-carousel/issues/434
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- needed
                // @ts-ignore
                emblaApi?.scrollTo(index as number, jump, index > selectedIndex ? -1 : 1)
            } else {
                if (emblaAutoplay?.isPlaying()) {
                    emblaAutoplay.stop()
                }
            }
            emblaAutoplay?.reset()
        },
        [emblaApi, selectedIndex, emblaAutoplay]
    )

    const scrollPrevious = useCallback(() => {
        emblaApi?.scrollPrev()
        emblaAutoplay?.reset()
    }, [emblaApi, emblaAutoplay])

    const scrollNext = useCallback(() => {
        emblaApi?.scrollNext()
        emblaAutoplay?.reset()
    }, [emblaApi, emblaAutoplay])

    const updateState = useCallback((api: EmblaCarouselType | undefined) => {
        if (!api) {
            return
        }

        setSlideCount(api.scrollSnapList().length)
        setSelectedIndex(api.selectedScrollSnap())
        setScrollPreviousDisabled(!api.canScrollPrev())
        setScrollNextDisabled(!api.canScrollNext())
    }, [])

    const toggleAutoPlay = useCallback(() => {
        if (!emblaAutoplay) {
            return
        }
        if (emblaAutoplay.isPlaying()) {
            emblaAutoplay.stop()
        } else {
            emblaAutoplay.play()
        }
    }, [emblaAutoplay])

    useEffect(() => {
        if (!emblaApi) {
            return
        }

        updateState(emblaApi)
        emblaApi.on('select', updateState)
        emblaApi.on('reInit', updateState)
        emblaApi.on('autoplay:play', () => setIsAutoPlaying(true))
        emblaApi.on('autoplay:stop', () => setIsAutoPlaying(false))
    }, [emblaApi, updateState])

    return {
        selectedIndex,
        slideCount,
        scrollTo,
        scrollPrevious,
        scrollNext,
        scrollPreviousDisabled,
        scrollNextDisabled,
        isAutoPlaying,
        toggleAutoPlay,
    }
}
